import React, { useState, useEffect, useRef, useMemo } from 'react';
import useStore from '../../../../store';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaArrowRight, FaArrowDown, FaRedoAlt, FaPause, FaPlay, FaAngleDoubleDown, FaTimes, FaShareAlt, FaGamepad } from 'react-icons/fa';
import UnifiedLeaderboard from '../../ui/UnifiedLeaderboard';
import GestureController from '../../ui/GestureController'; // <-- Импорт контроллера
import { useArcadeShare } from '../../ui/useArcadeShare';
import './Tetris.css';
import '../../ui/ArcadeShared.css';

// --- CONFIG ---

// Shapes: I, J, L, O, S, T, Z
const SHAPES = {
  I: { shape: [[0,0,0,0], [1,1,1,1], [0,0,0,0], [0,0,0,0]], color: 'type-I' },
  J: { shape: [[1,0,0], [1,1,1], [0,0,0]], color: 'type-J' },
  L: { shape: [[0,0,1], [1,1,1], [0,0,0]], color: 'type-L' },
  O: { shape: [[1,1], [1,1]], color: 'type-O' },
  S: { shape: [[0,1,1], [1,1,0], [0,0,0]], color: 'type-S' },
  T: { shape: [[0,1,0], [1,1,1], [0,0,0]], color: 'type-T' },
  Z: { shape: [[1,1,0], [0,1,1], [0,0,0]], color: 'type-Z' },
};
const SHAPE_KEYS = Object.keys(SHAPES);

const createEmptyGrid = (rows, cols) => Array.from({ length: rows }, () => Array(cols).fill(0));

const Tetris = ({ socket, onClose, timeLeft }) => {
  const { t } = useTranslation();
  const store = useStore.getState();

  // Получаем настройки от сервера
  const initialSession = useMemo(() => store.ref.info.game.arcadePublic?.session || {}, []);

  // Вычисляем динамические размеры сетки
  const[cols, rows] = useMemo(() => {
    return (initialSession.gridSize || '8x15').split('x').map(Number);
  }, [initialSession.gridSize]);

  // --- STATE ---
  const [grid, setGrid] = useState(createEmptyGrid(rows, cols)); // <-- Передаем размеры
  const [score, setScore] = useState(0);
  const [lines, setLines] = useState(0);
  const [level, setLevel] = useState(1);
  const [gameOver, setGameOver] = useState(false);
  const [showResults, setShowResults] = useState(false); // <--- НОВОЕ: Отдельный флаг для показа окна
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // <--- НОВОЕ СОСТОЯНИЕ
  const [nextPieceTick, setNextPieceTick] = useState(0); // Триггер для перерисовки превью

  const [finalData, setFinalData] = useState(null);

  // --- ИЗМЕНЕНИЕ 2: Инициализируем состояние интерфейса из базы пользователя ---
  const [hideControls, setHideControls] = useState(() => {
     return !!store.ref.info.user?.tetrisHideControls;
  });

  // ДОБАВЛЕНО: Стейт для визуальных эффектов
  const [effects, setEffects] = useState([]);
  const [isShaking, setIsShaking] = useState(false); // <-- ДОБАВЛЕНО: Стейт для тряски

  // Модульная функция для вызова эффекта
  const triggerEffect = (type, points, text) => {
    const id = Date.now() + Math.random();
    setEffects(prev => [...prev, { id, type, points, text }]);
    // Удаляем эффект из DOM после завершения анимации (2.5 сек)
    setTimeout(() => {
        setEffects(prev => prev.filter(e => e.id !== id));
    }, 2500);
  };

  // === МОДУЛЬ ШЕРИНГА ===
  const { boardRef: shareBoardRef, share, isSharing, isCapturing } = useArcadeShare(socket);
  // ======================

  // Refs
  const boardRef = useRef(null);
  const gridRef = useRef(null); // <--- НОВЫЙ REF ДЛЯ СТАКАНА
  const gestureControllerRef = useRef(null); // <--- REF ДЛЯ КОНТРОЛЛЕРА
  const isInitializedRef = useRef(false); // <--- ДОБАВЛЕНО

  const gameState = useRef({
    grid: createEmptyGrid(rows, cols), // <-- Передаем размеры
    piece: null,
    nextPiece: null,
    score: 0,
    lines: 0,

    // ИСПРАВЛЕНИЕ: Берем уровень из сессии или 1
    level: initialSession.level || 1,

    // ВАЖНО: Запоминаем стартовые значения для правильного расчета прогрессии
    initialLevel: initialSession.level || 1,
    initialSpeed: initialSession.speed || 800, // Если сервер не прислал, то 800

    isGameOver: false,

    // Текущая скорость равна начальной
    speed: initialSession.speed || 800,

    lastTime: 0,
    dropAccumulator: 0,
    isPaused: false // Добавлено, если этого поля не было в исходнике
  });

  const requestRef = useRef();

  // --- ENGINE ---

  const getRandomPiece = () => {
    const key = SHAPE_KEYS[Math.floor(Math.random() * SHAPE_KEYS.length)];
    const def = SHAPES[key];
    return {
      shape: def.shape,
      color: def.color,
      // Центрируем с учетом текущей ширины
      x: Math.floor(cols / 2) - Math.ceil(def.shape[0].length / 2),
      y: 0
    };
  };

  const checkCollision = (piece, gridData, offsetX = 0, offsetY = 0) => {
    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x] !== 0) {
          const newX = piece.x + x + offsetX;
          const newY = piece.y + y + offsetY;
          if (newX < 0 || newX >= cols || newY >= rows) return true; // <-- Используем cols и rows
          if (newY >= 0 && gridData[newY][newX] !== 0) return true;
        }
      }
    }
    return false;
  };

  const mergePiece = () => {
    const { piece, grid } = gameState.current;
    const newGrid = grid.map(row => [...row]);

    for (let y = 0; y < piece.shape.length; y++) {
      for (let x = 0; x < piece.shape[y].length; x++) {
        if (piece.shape[y][x] !== 0) {
           const ny = piece.y + y;
           const nx = piece.x + x;
           // Защита от выхода за рамки массива по новым размерам
           if (ny >= 0 && ny < rows && nx >= 0 && nx < cols) {
               newGrid[ny][nx] = piece.color;
           }
        }
      }
    }
    return newGrid;
  };

  const clearLines = (grid) => {
    let cleared = 0;
    const newGrid = grid.filter(row => {
      if (row.every(cell => cell !== 0)) {
        cleared++;
        return false;
      }
      return true;
    });

    while (newGrid.length < rows) {
      newGrid.unshift(Array(cols).fill(0));
    }

    if (cleared > 0) {
      const pointsTable = [0, 1, 3, 6, 12];
      let points = pointsTable[cleared] || 0;

      // Проверка на полную очистку (Perfect Clear)
      const isPerfectClear = newGrid.every(row => row.every(cell => cell === 0));

      if (isPerfectClear) {
          points += 10;
          triggerEffect('perfect_clear', `+${points}`, t('arcade.tetris_perfect_clear', 'CLEARED!'));
      } else if (cleared === 4) {
          triggerEffect('tetris', `+${points}`, 'TETRIS!');
      } else if (cleared === 3) {
          triggerEffect('triple', `+${points}`, t('arcade.tetris_triple', 'TRIPLE!'));
      } else if (cleared === 2) {
          // Для 2 линий - только очки, без текста
          triggerEffect('double', `+${points}`, '');
      } else if (cleared === 1) {
          // Для 1 линии - самый скромный эффект
          triggerEffect('single', `+${points}`, '');
      }

      // ДОБАВЛЕНО: Легкая тряска стакана при любом удалении линий
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 200);

      gameState.current.score += points;
      gameState.current.lines += cleared;

      const levelsGained = Math.floor(gameState.current.lines / 5);
      gameState.current.level = gameState.current.initialLevel + levelsGained;

      const speedDecrease = levelsGained * 20;
      gameState.current.speed = Math.max(50, gameState.current.initialSpeed - speedDecrease);

      setScore(gameState.current.score);
      setLines(gameState.current.lines);
      setLevel(gameState.current.level);

      // Отправка на сервер с флагом полной очистки
      store.ref.work.methods.hostAction('mafiaMiniGameAction', {
          type: 'score_update',
          linesCleared: cleared,
          isPerfectClear: isPerfectClear
      });
    }

    return newGrid;
  };

  const spawnPiece = () => {
    const st = gameState.current;
    st.piece = st.nextPiece || getRandomPiece();
    st.nextPiece = getRandomPiece();
    setNextPieceTick(t => t + 1); // Обновляем UI превью

    // FIX: Если коллизия сразу при спавне - это Game Over
    // Но мы должны дать возможность отрисоваться
    if (checkCollision(st.piece, st.grid)) {
       // Форсируем обновление UI, чтобы показать эту фигуру (смертельную)
       setGrid([...st.grid]);
       handleGameOver();
    }
  };

  const handleGameOver = () => {
    if (gameState.current.isGameOver) return;

    gameState.current.isGameOver = true;
    setIsPlaying(false);
    setGameOver(true);
    window.croco?.haptic?.('error');

    // ИЗМЕНЕНИЕ: Теперь передаем серверу последний кадр поля и фигур
    store.ref.work.methods.hostAction('mafiaMiniGameAction', {
        type: 'finish',
        grid: gameState.current.grid,
        piece: gameState.current.piece,
        nextPiece: gameState.current.nextPiece
    });
  };

  // --- ACTIONS ---

  const togglePause = () => {
    if (gameOver) return;
    const nextState = !gameState.current.isPaused;

    setIsPaused(nextState);
    gameState.current.isPaused = nextState;

    if (!nextState) {
        gameState.current.lastTime = performance.now();
    }
  };

  // --- ИЗМЕНЕНИЕ 3: Функция переключения и сохранения настройки ---
  const toggleControls = () => {
      const newVal = !hideControls;
      setHideControls(newVal);
      // Сохраняем настройку в БД пользователя на сервере
      store.ref.work.methods.saveUserSets({ tetrisHideControls: newVal });
      window.croco?.haptic?.('selection');
  };

  // Функция "Остаться" (Песочница)
  const handleStay = () => {
      setShowResults(false);
      // Снимаем паузу логически и визуально
      setIsPaused(false);
      gameState.current.isPaused = false;
      gameState.current.lastTime = performance.now(); // Сброс дельты времени, чтобы фигура не прыгнула
  };

  const move = (dir) => { // -1 or 1
    // Блокируем управление, если пауза в рефе активна
    if (gameState.current.isGameOver || gameState.current.isPaused) return;
    const { piece, grid } = gameState.current;
    if (!checkCollision(piece, grid, dir, 0)) {
       piece.x += dir;
       syncState();
       // Убрали вибрацию при каждом шаге свайпа, так как это too much для непрерывного режима
    }
  };

  const rotate = () => {
    if (gameState.current.isGameOver || gameState.current.isPaused) return;
    const { piece, grid } = gameState.current;
    const rotatedShape = piece.shape[0].map((_, index) => piece.shape.map(row => row[index]).reverse());

    // Простейшая Wall Kick (проверка сдвига, если вращение задевает стену)
    const testPiece = { ...piece, shape: rotatedShape };

    let offset = 0;
    if (checkCollision(testPiece, grid, 0, 0)) {
        // Try shifting left/right
        if (!checkCollision(testPiece, grid, 1, 0)) offset = 1;
        else if (!checkCollision(testPiece, grid, -1, 0)) offset = -1;
        else return; // Cannot rotate
    }

    piece.shape = rotatedShape;
    piece.x += offset;
    syncState();
    window.croco?.haptic?.('selection');
  };

  const drop = () => {
    if (gameState.current.isGameOver || gameState.current.isPaused) return;
    const { piece, grid } = gameState.current;

    if (!checkCollision(piece, grid, 0, 1)) {
        piece.y += 1;
        gameState.current.dropAccumulator = 0; // Reset drop timer on manual move
    } else {
        lockPiece();
    }
    syncState();
  };

  const lockPiece = () => {
     const st = gameState.current;
     st.grid = mergePiece();
     st.grid = clearLines(st.grid);
     spawnPiece(); // Внутри генерируются новые piece и nextPiece
     window.croco?.haptic?.('impactLight');
     setGrid(st.grid);

     // НОВОЕ: Отправляем полный слепок стейта на сервер (стакан + обе фигуры)
     store.ref.work.methods.hostAction('mafiaMiniGameAction', {
         type: 'sync_state',
         grid: st.grid,
         piece: st.piece,
         nextPiece: st.nextPiece
     });

     if (gestureControllerRef.current) {
         gestureControllerRef.current.cancelGesture();
     }
  };

  const hardDrop = () => {
    // ВАЖНО: Добавлена проверка на паузу
    if (gameState.current.isGameOver || gameState.current.isPaused) return;

    const { piece, grid } = gameState.current;
    while (!checkCollision(piece, grid, 0, 1)) {
        piece.y += 1;
    }
    lockPiece();
    syncState();
    window.croco?.haptic?.('impactHeavy');
  };

  // Обновляем локальные стейты при старте
  useEffect(() => {
    if (initialSession) {
        setLevel(initialSession.level || 1);
        setScore(initialSession.score || 0);
        setLines(initialSession.lines || 0);
    }
  }, [initialSession]);

  // Force React re-render for piece movement
  const [tick, setTick] = useState(0);
  const syncState = () => setTick(t => t + 1);

  // --- LOOP ---
  const loop = (time) => {
    requestRef.current = requestAnimationFrame(loop);

    // Читаем isPaused из REF, а не из замыкания
    if (gameState.current.isGameOver) return;

    if (gameState.current.isPaused) {
        // Обновляем lastTime, чтобы время "не шло" пока мы стоим
        gameState.current.lastTime = time;
        return;
    }

    if (!isPlaying) return;

    const st = gameState.current;
    const deltaTime = time - st.lastTime;
    st.lastTime = time;

    st.dropAccumulator += deltaTime;
    if (st.dropAccumulator > st.speed) {
        // Логика падения внутри функции drop, она же делает syncState
        // Но здесь мы вызываем "тихое" падение или дублируем логику?
        // Лучше вызвать функцию, она проверит коллизии

        // ВАЖНО: drop() использует gameState.current, так что всё ок
        if (!checkCollision(st.piece, st.grid, 0, 1)) {
            st.piece.y += 1;
            st.dropAccumulator = 0;
            syncState();
        } else {
            lockPiece();
        }
    }
  };

  // --- LIFECYCLE ---

  useEffect(() => {
    if (!socket) return;
    store.ref.work.methods.hostAction('mafiaMiniGameAction', { type: 'init' });

    const handleEvent = (data) => {
        // ВОССТАНОВЛЕНИЕ СОСТОЯНИЯ
        if (data.type === 'update') {
            const st = data.state;
            if (st) {
                // 1. Загружаем серверные данные
                if (st.grid) {
                    gameState.current.grid = st.grid.map(row => [...row]);
                    setGrid(gameState.current.grid);
                }
                if (st.piece) gameState.current.piece = st.piece;
                if (st.nextPiece) {
                    gameState.current.nextPiece = st.nextPiece;
                    setNextPieceTick(t => t + 1);
                }

                setScore(st.score);
                setLines(st.lines);
                setLevel(st.level);
                gameState.current.score = st.score;
                gameState.current.lines = st.lines;
                gameState.current.level = st.level;
                if (st.speed) gameState.current.speed = st.speed;

                // 2. Если игрок умер — пресекаем игру
                if (st.isDead) {
                    gameState.current.isGameOver = true;
                    setIsPlaying(false);
                    setGameOver(true);
                    setShowResults(true); // Сразу показываем финальное табло
                }
                // 3. Старт игры ТОЛЬКО после получения ответа сервера
                else if (!isInitializedRef.current && !gameState.current.isGameOver) {
                    isInitializedRef.current = true;

                    // Если игра новая (на сервере нет сохраненной фигуры)
                    if (!gameState.current.piece) {
                        gameState.current.nextPiece = getRandomPiece();
                        spawnPiece();

                        // СРАЗУ сохраняем первые фигуры на сервер, чтобы нельзя было "сбросить" самую первую деталь
                        store.ref.work.methods.hostAction('mafiaMiniGameAction', {
                            type: 'sync_state',
                            grid: gameState.current.grid,
                            piece: gameState.current.piece,
                            nextPiece: gameState.current.nextPiece
                        });
                    } else {
                        // Форсируем UI для восстановленных фигур
                        setNextPieceTick(t => t + 1);
                        setGrid([...gameState.current.grid]);
                    }

                    setIsPlaying(true);
                    gameState.current.lastTime = performance.now();
                    requestRef.current = requestAnimationFrame(loop);
                }
            }
        }
        else if (data.type === 'finish') {
            setFinalData(data.finalData);

            // Если сервер сразу ответил финишем (мертвый игрок перезашел)
            if (data.state?.isDead) {
                gameState.current.isGameOver = true;
                setIsPlaying(false);
                setGameOver(true);
                setShowResults(true);

                if (data.state.grid) {
                    gameState.current.grid = data.state.grid.map(row =>[...row]);
                    setGrid(gameState.current.grid);
                }
                if (data.state.nextPiece) {
                    gameState.current.nextPiece = data.state.nextPiece;
                    setNextPieceTick(t => t + 1);
                }
            }
        }
        else if (data.type === 'game_over') {
            setFinalData(data.finalData || { my: data.my, leaderboard: data.leaderboard });

            if (!gameState.current.isGameOver) {
                gameState.current.isPaused = true;
                setIsPaused(true);
                setShowResults(true);
                window.croco?.haptic?.('warning');
            } else {
                setGameOver(true);
            }
        }
    };

    socket.on('arcade:event', handleEvent);

    return () => {
        if (requestRef.current) cancelAnimationFrame(requestRef.current);
        socket.off('arcade:event', handleEvent);
    };
  }, [socket]); // Обрати внимание, локальный блок запуска Start Game Local отсюда удален

  // Перезапуск лупа при изменении isPlaying
  useEffect(() => {
     if (isPlaying && !gameState.current.isGameOver) {
         gameState.current.lastTime = performance.now();
         requestRef.current = requestAnimationFrame(loop);
     }
     return () => cancelAnimationFrame(requestRef.current);
  }, [isPlaying]);

  // Keyboard controls
  useEffect(() => {
    const handleKey = (e) => {
        if (gameState.current.isGameOver) return;

        // Используем e.code вместо e.key, чтобы не зависеть от раскладки (RU/EN)
        // KeyW работает всегда, даже если печатается 'ц'
        switch(e.code) {
            case 'ArrowLeft':
            case 'KeyA':
                move(-1);
                break;

            case 'ArrowRight':
            case 'KeyD':
                move(1);
                break;

            case 'ArrowDown':
            case 'KeyS':
                drop(); // Мягкий спуск
                break;

            case 'ArrowUp':
            case 'KeyW':
                rotate();
                break;

            case 'Space':
                hardDrop(); // Хард дроп
                break;

            default: break;
        }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // --- GESTURE CONTROLLER INTEGRATION ---
  useEffect(() => {
    // ВАЖНО: Вешаем контроллер только на gridRef (зону стакана)
    if (!gridRef.current) return;

    const controller = new GestureController(gridRef.current, {
      continuous: true,
      sensitivityX: 25,
      sensitivityY: 15,
      disableDiagonal: true,

      // НОВОЕ: Включаем блокировку оси, чтобы жест вниз не сбивал фигуру вбок
      lockAxis: true,
      axisLockThreshold: 10 // Через 10px определяем ось и блокируем другую
    });

    // Сохраняем в реф, чтобы иметь доступ в lockPiece
    gestureControllerRef.current = controller;

    // Настройка коллбеков
    controller.onSwipe = (direction) => {
      switch (direction) {
        case 'left':
          move(-1);
          break;
        case 'right':
          move(1);
          break;
        case 'down':
          drop(); // Мягкий спуск
          break;
        case 'up':
          // Можно использовать для Hard Drop, но пока оставим пустым, чтобы не мешать
          break;
        default:
          break;
      }
    };

    // Тап по зоне
    controller.onTap = (zone) => {
      // Любой тап вращает, или можно разделить: верх - хард дроп, остальное - поворот
      rotate();
    };

    return () => {
      controller.destroy();
      gestureControllerRef.current = null;
    };
  }, []);

  // --- RENDER HELPERS ---

  // Merge active piece into grid for display
  const displayGrid = grid.map(row => [...row]);

  if (gameState.current.piece) {
      const p = gameState.current.piece;
      p.shape.forEach((row, dy) => {
          row.forEach((val, dx) => {
              if (val !== 0) {
                  const y = p.y + dy;
                  const x = p.x + dx;
                  if (y >= 0 && y < rows && x >= 0 && x < cols) { // <-- Используем rows и cols
                      displayGrid[y][x] = p.color;
                  }
              }
          });
      });
  }

  // --- ИЗМЕНЕНИЕ 4: Обновляем инлайн-стили стакана ---
  // В районе 480-490 строки:
  const gridStyle = {
    gridTemplateRows: `repeat(${rows}, 1fr)`,
    gridTemplateColumns: `repeat(${cols}, 1fr)`,
    aspectRatio: `${cols} / ${rows}`,
    // Точный математический расчет, гарантирующий, что стакан впишется по ширине
    maxHeight: `calc(min(96vw - 110px, 283px) * (${rows} / ${cols}))`,
    '--bg-size-x': `${100 / cols}%`,
    '--bg-size-y': `${100 / rows}%`
  };

  const renderNextPiece = () => {
     const piece = gameState.current.nextPiece;
     if (!piece) return null;

     const previewGrid = Array(4).fill(null).map(() => Array(4).fill(0));
     const shape = piece.shape;

     // Центрирование в 4x4
     const offsetY = Math.floor((4 - shape.length) / 2);
     const offsetX = Math.floor((4 - shape[0].length) / 2);

     shape.forEach((row, y) => {
        row.forEach((val, x) => {
           if (val !== 0) {
              previewGrid[y + offsetY][x + offsetX] = piece.color;
           }
        });
     });

     return previewGrid.map((row, y) =>
        row.map((cell, x) => (
           <div key={`n-${y}-${x}`} className={cell !== 0 ? `mini-cell cell-filled ${cell}` : 'mini-cell'} />
        ))
     );
  };

  // --- НОВЫЙ ФИНАЛЬНЫЙ ЭКРАН (СТИЛЬ ДОСЬЕ) ---
  // ДОБАВЛЯЕМ && !isCapturing, чтобы скрыть меню в момент вспышки камеры
  if ((gameOver || showResults) && !isCapturing) {
      const myStats = finalData?.my || {};
      const currentScore = myStats.score !== undefined ? myStats.score : score;
      const currentLines = myStats.lines !== undefined ? myStats.lines : lines;
      const rank = myStats.rank || 'tetris_rank_rookie';

      // Определяем заголовок и штамп
      const isDead = gameOver;
      const reportTitle = isDead ? t('arcade.tetris_game_over', 'АРХИВ ПЕРЕПОЛНЕН') : t('arcade.time_up', 'СМЕНА ОКОНЧЕНА');
      const stampText = isDead ? t('arcade.tetris_result_goodbye', "УВОЛЕН") : t("arcade.tetris_result_accepted", "ПРИНЯТО");
      const stampClass = isDead ? "tr-stamp-fail" : "tr-stamp-ok";

      return (
        <div className="tr-report-overlay">
           <div className="tr-report-card">
               {/* Водяной знак */}
               <div className="tr-watermark">ARCHIVE</div>

               {/* Заголовок */}
               <div className="tr-report-header">
                  <div className="tr-report-title">{reportTitle}</div>
               </div>

               {/* Сетка статистики */}
               <div className="tr-stats-grid">
                   <div className="tr-stat-row">
                       <span className="tr-stat-label">{t('arcade.wordduel_rank', 'ЗВАНИЕ')}</span>
                       <span className="tr-stat-value rank-text">{t(`arcade.${rank}`, rank)}</span>
                   </div>
                   <div className="tr-stat-row">
                       <span className="tr-stat-label">{t('arcade.tetris_score', 'СЧЕТ')}</span>
                       <span className="tr-stat-value">{currentScore}</span>
                   </div>
                   <div className="tr-stat-row" style={{borderBottom: 'none'}}>
                       <span className="tr-stat-label">{t('arcade.tetris_lines', 'ЛИНИИ')}</span>
                       <span className="tr-stat-value">{currentLines}</span>
                   </div>
               </div>

               {/* Штамп */}
               <div className={`tr-result-stamp ${stampClass}`}>
                   {stampText}
               </div>

               {/* Лидерборд */}
               <div style={{width: '100%', marginBottom: '15px'}}>
                   {finalData && (
                       <UnifiedLeaderboard
                          results={finalData.leaderboard}
                          myId={store.ref.telegram.telegramUserId}
                          t={t}
                       />
                   )}
               </div>

               {/* Кнопки */}
               {/* Кнопки */}
               <div className="tr-btn-group">
                   {/* === НОВАЯ КНОПКА ПОДЕЛИТЬСЯ === */}
                   <button
                       className="tr-btn tr-btn-primary"
                       style={{ background: '#3b82f6', color: '#fff', marginBottom: '5px' }}
                       onClick={() => share(myStats, { backgroundColor: '#222' })}
                       disabled={isSharing}
                   >
                       <FaShareAlt style={{marginRight: '8px'}} />
                       {isSharing ? t('arcade.share_preparing') : t('arcade.share')}
                   </button>
                   {/* ============================== */}

                   {/* Кнопка "Остаться" только если жив */}
                   {!isDead && (
                       <button className="tr-btn tr-btn-secondary" onClick={handleStay}>
                           <FaPlay style={{marginRight: '8px'}} /> {t('arcade.stay', 'ОСТАТЬСЯ')}
                       </button>
                   )}

                   <button className="tr-btn tr-btn-primary" onClick={onClose}>
                       <FaTimes style={{marginRight: '8px'}} /> {t('arcade.close', 'ЗАКРЫТЬ')}
                   </button>
               </div>
           </div>
        </div>
      );
  }

  return (
    // Добавляем динамический класс no-controls
    <div className={`tetris-board-container ${hideControls ? 'no-controls' : ''}`} ref={shareBoardRef} style={{ touchAction: 'none' }}>
       <div className="tetris-header">
          <div className="tetris-title">{t('arcade.intro_tetris_title', 'УПАКОВЩИК УЛИК')}</div>
          <div className="tetris-timer">{(timeLeft / 60) | 0}:{(timeLeft % 60).toString().padStart(2, '0')}</div>
       </div>

       <div className="tetris-main-area">
          {/* Main Grid: ДОБАВЛЕН КЛАСС tetris-shake */}
          <div className={`tetris-grid ${isShaking ? 'tetris-shake' : ''}`} ref={gridRef} style={gridStyle}>

             {/* ЭФФЕКТЫ ВЕРНУЛИСЬ В ЦЕНТР СТАКАНА */}
             {effects.map(eff => (
                 <div key={eff.id} className={`tetris-fx fx-${eff.type}`}>
                     {eff.text && <div className="fx-text">{eff.text}</div>}
                     <div className="fx-points">{eff.points}</div>
                 </div>
             ))}

             {/* ОВЕРЛЕЙ ПАУЗЫ */}
             {isPaused && (
                 <div className="tetris-paused-overlay">
                    <div className="paused-text">ПАУЗА</div>
                 </div>
             )}

             {displayGrid.map((row, y) => (
                 row.map((cell, x) => (
                     <div key={`${y}-${x}`} className={`tetris-cell ${cell !== 0 ? `cell-filled ${cell}` : ''}`} />
                 ))
             ))}
          </div>

          {/* Sidebar */}
          <div className="tetris-sidebar">
             {/* 1. СЛЕДУЮЩАЯ ФИГУРА (Теперь сверху) */}
             <div className="stat-group">
                <div className="sb-label">{t('arcade.tetris_next', 'NEXT')}</div>
                <div className="next-piece-container">
                   <div className="next-piece-grid">
                      {renderNextPiece()}
                   </div>
                </div>
             </div>

             {/* 2. СТАТИСТИКА (Без громоздких фонов) */}
             <div className="stat-group">
                <div className="sb-label">{t('arcade.tetris_score', 'SCORE')}</div>
                <div className="sb-val">{score}</div>
             </div>

             <div className="stat-group">
                <div className="sb-label">{t('arcade.tetris_lines', 'LINES')}</div>
                <div className="sb-val">{lines}</div>
             </div>

             <div className="stat-group">
                <div className="sb-label">{t('arcade.tetris_level', 'LEVEL')}</div>
                <div className="sb-val">{level}</div>
             </div>

             {/* КНОПКИ УПРАВЛЕНИЯ (В самом низу) */}
             <div style={{ marginBottom: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                 <div
                   className={`sidebar-btn ${hideControls ? 'controls-off' : ''}`}
                   onClick={toggleControls}
                   title={t('arcade.tetris_toggle_controls', 'Переключить управление')}
                 >
                    <FaGamepad className="icon-gamepad" />
                 </div>

                 <div
                   className={`sidebar-btn ${isPaused ? 'paused' : ''}`}
                   onClick={togglePause}
                   title="Pause Game"
                 >
                    {isPaused ? <FaPlay /> : <FaPause />}
                 </div>
             </div>
          </div>
       </div>

       {/* --- ИЗМЕНЕНИЕ 7: Скрываем панель управления, если включен hideControls --- */}
       {!hideControls && (
           <div className="tetris-controls">
              <div className="t-btn btn-hard-drop" onPointerDown={() => hardDrop()}>
                  <FaAngleDoubleDown />
              </div>
              <div className="t-btn btn-rotate" onPointerDown={() => rotate()}>
                  <FaRedoAlt />
              </div>
              <div className="t-btn btn-left" onPointerDown={() => move(-1)}>
                  <FaArrowLeft />
              </div>
              <div className="t-btn btn-down" onPointerDown={() => drop()}>
                  <FaArrowDown />
              </div>
              <div className="t-btn btn-right" onPointerDown={() => move(1)}>
                  <FaArrowRight />
              </div>
           </div>
       )}

    </div>
  );
};

export default Tetris;